import React, { useEffect, useState } from "react";
import Layout from "component/layout/Layout";
import prequal_frame from "assets/images/prequal-frame.png";
import styles from "./Prequalification.module.css";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import document_icon from "assets/images/document-icon.svg";
import Grid from "@mui/material/Grid";
import AppButton from "component/button/AppButton";
import { Container } from "@mui/system";
import TextField from "component/textField/TextField";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import ValidationMessage from "component/validationMessage/ValidationMessage";
import AppBackdrop from "component/appBackdrop/AppBackdrop";
import PrequalFailureModal from "component/PrequalFailureModal/PrequalFailureModal";
import { useMortgageCalc } from "api/mortgageCalc";
import { monthsToYears, numberWithCommas } from "utils/helpers";
import { useRef } from "react";

import AuthModalLayout from "component/authModalLayout/AuthModalLayout";
import { useNavigate } from "react-router-dom";
import { prequalSchema } from "schemas/prequal/Prequal";
import AppDatePicker from "component/inputs/datePicker/DatePicker";

function Prequalification() {
  const navigate = useNavigate();
  const [showEmployed, setShowEmployed] = useState(false);
  const [showSelfEmployed, setShowSelfEmployed] = useState(false);
  const [showOtherLoan, setShowOtherLoan] = useState(false);
  const [tenorMessage, setTenorMessage] = useState(null);
  const pageRef = useRef(null);

  const [visible, setVisible] = useState(true);

  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(prequalSchema),
    defaultValues: { interestRate: 28.50 },
  });

  const DOB = watch("dob", "");
  const propertyPrice = watch("propertyPrice", null);
  const increaseValue = watch("increase", 0);
  const min = watch("minimum_equity_contribution", null);

  useEffect(() => {
    if (propertyPrice > 0) {
      setValue("minimum_equity_contribution", propertyPrice * 0.2);
    }
  }, [propertyPrice]);

  const {
    isLoading,
    mutate,
    openModal,
    setOpenModal,
    data: mortgageResp,
    openFailure,
    setOpenFailure,
  } = useMortgageCalc();

  const submit = (data) => {
    const payload = {
      ...data,
      otherMonthlyLoan: parseInt(data?.otherMonthlyLoan || 0),
      equityContribution:
        increaseValue < min ? parseInt(min) : parseInt(increaseValue),
    };

    mutate(payload);
    reset();
    setShowEmployed(false);
    setShowOtherLoan(false);
    //setVisible(false);
    // setTimeout(() => {
    //   setVisible(true);
    // }, 200);
  };

  useEffect(() => {
    if (!!DOB) {
      const dobDate = new Date(DOB);
      const today = new Date(Date.now());
      const age = today.getFullYear() - dobDate.getFullYear();

      let timeLeft =
        60 * 12 + today.getMonth() - (age * 12 + dobDate.getMonth());

      if (timeLeft < -720) {
        setTenorMessage(null);
      } else if (timeLeft > 720) {
        setTenorMessage(
          "Please enter a valid Date of birth to see the maximum tenor allowed"
        );
      } else if (timeLeft < 1) {
        setTenorMessage("You are past the age for a mortgage");
      } else if (timeLeft >= 1 && timeLeft <= 720) {
        setTenorMessage(
          `NB: Maximum tenor allowed for you is ${
            timeLeft > 240 ? 240 : timeLeft
          } months`
        );
      }
    }
  }, [DOB]);

  useEffect(() => {
    if (typeof window !== "undefined" && mortgageResp?.status) {
      if (window?.innerWidth > 900) {
        pageRef.current.scrollIntoView({
          behaviour: "smooth",
          block: "start",
        });
      } else
        pageRef.current.scrollIntoView({ behaviour: "smooth", block: "end" });
    }
  }, [mortgageResp]);

  const routeToSignUp = () => {
    navigate("/signup");
  };

  return (
    <Layout activeLink="prequalification">
      <div className={styles.prequal} ref={pageRef}>
        <Container>
          <Grid container rowGap="6" justifyContent={["center", "center"]}>
            <Grid xs={12} lg={8} justifyContent={["center", "center"]}>
              <div className={styles.prequal_form}>
                <form onSubmit={handleSubmit(submit)}>
                  <div className={styles.heading}>
                    <div>
                      <Typography>Mortgage Calculator</Typography>
                      <p className={styles.subHeading}>
                        Use the Mortgage Calculator to determine the maximum
                        loan amount and monthly repayment you can access for
                        your mortgage.
                      </p>
                    </div>
                    <img src={document_icon} alt="" />
                  </div>

                  <div className={styles.body}>
                    <Grid
                      container
                      spacing={2}
                      overflow={"scroll"}
                      // className={styles.scrollbar_hidden}
                    >
                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <TextField
                            label="Email"
                            type="email"
                            placeholder="user@email.com"
                            name="email"
                            register={register}
                            errors={errors}
                          />
                        </div>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <TextField
                            name="propertyPrice"
                            errors={errors}
                            register={register}
                            type="text"
                            placeholder="10,000,000"
                            label="Property Price"
                          />
                        </div>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <AppDatePicker
                            errors={errors}
                            name="dob"
                            label="Date of Birth"
                            setValue={setValue}
                            value={DOB}
                            trigger={trigger}
                          />
                        
                        </div>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <TextField
                            errors={errors}
                            register={register}
                            type="text"
                            disabled
                            placeholder="....."
                            label="Minimum Equity Contribution"
                            name="minimum_equity_contribution"
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <TextField
                            errors={errors}
                            name="increase"
                            register={register}
                            // disabled
                            type="number"
                            placeholder="..."
                            label="Equity Contribution"
                          />
                        </div>
                        {(increaseValue > 0 || increaseValue < 0) &&
                          increaseValue < min && (
                            <Box>
                              <ValidationMessage
                                message={
                                  "Entered amount must be greater than minimum equity contribution"
                                }
                              />
                            </Box>
                          )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <TextField
                            name="interestRate"
                            errors={errors}
                            register={register}
                            type="text"
                            placeholder="28.50%"
                            label="Interest Rate"
                            disabled
                          />
                        </div>
                      </Grid>
                    
                      {visible && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <div className={styles.field}>
                              <div className={styles.selections}>
                                <div className={styles.selections_label}>
                                  <Typography>Employment Type</Typography>
                                </div>
                                <Controller
                                  rules={{ required: true }}
                                  control={control}
                                  name="employmentType"
                                  render={({ field }) => (
                                    <RadioGroup {...field}>
                                      <div className={styles.itemBox}>
                                        <FormControlLabel
                                          className={styles.item}
                                          control={
                                            <Radio
                                              onChange={() => {
                                                setShowEmployed(true);
                                                setShowSelfEmployed(false);
                                              }}
                                              size="8px"
                                              value={"employed"}
                                            />
                                          }
                                          label={<p>Employed</p>}
                                        />
                                        {showEmployed && (
                                          <TextField
                                            type="text"
                                            placeholder="₦ Monthly Net Salary"
                                            name="netSalary"
                                            register={register}
                                            errors={errors}
                                          />
                                        )}
                                      </div>
                                      <div className={styles.itemBox}>
                                        <FormControlLabel
                                          className={styles.item}
                                          control={
                                            <Radio
                                              size="8px"
                                              value={"self-employed"}
                                              onChange={() => {
                                                setShowEmployed(false);
                                                setShowSelfEmployed(true);
                                              }}
                                            />
                                          }
                                          label={<p>Self-employed</p>}
                                        />
                                        {showSelfEmployed && (
                                          <TextField
                                            type="text"
                                            placeholder="₦ Monthly Income"
                                            name="netSalary"
                                            register={register}
                                            errors={errors}
                                          />
                                        )}
                                      </div>
                                    </RadioGroup>
                                  )}
                                />
                                <ValidationMessage
                                  message={errors?.employmentType?.message}
                                />
                              </div>
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <div className={styles.field}>
                              <div className={styles.selections}>
                                <div className={styles.selections_label}>
                                  <Typography>Any other loan?</Typography>
                                </div>
                                <Controller
                                  rules={{ required: true }}
                                  control={control}
                                  name="otherLoan"
                                  render={({ field }) => (
                                    <RadioGroup {...field}>
                                      <div className={styles.itemBox}>
                                        <FormControlLabel
                                          className={styles.item}
                                          control={
                                            <Radio
                                              size="8px"
                                              value={true}
                                              onChange={() => {
                                                setShowOtherLoan(true);
                                              }}
                                            />
                                          }
                                          label={<p>Yes</p>}
                                        />
                                        {showOtherLoan && (
                                          <TextField
                                            type="number"
                                            placeholder="₦ Repayment Value"
                                            name="otherMonthlyLoan"
                                            register={register}
                                            errors={errors}
                                          />
                                        )}
                                      </div>
                                      <div className={styles.itemBox}>
                                        <FormControlLabel
                                          className={styles.item}
                                          control={
                                            <Radio
                                              size="8px"
                                              value={false}
                                              onChange={() => {
                                                setShowOtherLoan(false);
                                              }}
                                            />
                                          }
                                          label={<p>No</p>}
                                        />
                                      </div>
                                    </RadioGroup>
                                  )}
                                />
                                <ValidationMessage
                                  // @ts-ignore
                                  message={errors?.otherLoan?.message}
                                />
                              </div>
                            </div>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} sm={6}>
                        <div className={styles.field}>
                          <TextField
                            name="tenor"
                            errors={errors}
                            register={register}
                            type="text"
                            placeholder="240 months"
                            label="Tenor (Months)"
                          />
                          {!!DOB && DOB?.length > 0 && (
                            <ValidationMessage message={tenorMessage} />
                          )}
                        </div>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <div className={styles.btn}>
                          <AppButton
                            width="149px"
                            btnText="Submit"
                            textColor="white"
                            btnBg="#0089FF"
                            type={"submit"}
                            loading={isLoading}
                            disabled={
                              (increaseValue > 0 || increaseValue < 0) &&
                              increaseValue < min &&
                              true
                            }
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </form>
              </div>
            </Grid>
            {!!mortgageResp && mortgageResp?.data?.eligible && (
              // mortgageResp?.data?.computed?.repayments?.length > 0 &&
              <Grid xs={12} lg={4}>
                <MortgageSummary
                  mortgageDetails={mortgageResp?.data?.computed}
                />
              </Grid>
            )}
          </Grid>
        </Container>

        {/* <div className={styles.prequal_frame}>
          <img src={prequal_frame} alt="" />
        </div> */}
      </div>

      <AppBackdrop
        openModal={openModal}
        handleCloseModal={() => {}}
        children={
          <AuthModalLayout
            handleClose={() => {
              setOpenModal(false);
              routeToSignUp();
            }}
            icon={"success"}
            primaryText="Success"
            btnText="Proceed to Application"
            secondaryText={`Congratulations, you are eligible for a mortgage of N${numberWithCommas(
              mortgageResp?.data?.computed?.loanAmount
            )} for ${mortgageResp?.data?.computed?.tenor} months`}
            closeBtn={() => setOpenModal(false)}
          />
        }
      />
      <AppBackdrop
        openModal={openFailure}
        handleCloseModal={() => {}}
        children={
          <PrequalFailureModal
            handleClose={() => setOpenFailure(false)}
            setOpenModal={setOpenModal}
          />
        }
      />
    </Layout>
  );
}

export default Prequalification;

const MortgageSummary = ({ mortgageDetails, setOpenModal }) => {
  return (
    <Container>
      <div className={styles.summary}>
        <div className={styles.amount}>
          <Typography variant="h3">Amount:</Typography>
          <Typography variant="h2">
            ₦ {numberWithCommas(mortgageDetails?.loanAmount)}
          </Typography>
        </div>

        <div className={styles.more}>
          <div>
            <Typography variant="h5">Tenor</Typography>
            <Typography>{monthsToYears(mortgageDetails?.tenor)}</Typography>
          </div>
          <div>
            <Typography variant="h5">Max Monthly Repayment:</Typography>
            <Typography>
              ₦ {numberWithCommas(mortgageDetails?.repayments[0]?.repayment)}
            </Typography>
          </div>
        </div>

        <div style={{ padding: "30px" }}>
          <AppButton
            handleAction={() => {
              setOpenModal(false);
            }}
            height="40px"
            fullwidth
            textColor="#FFFFFF"
            btnBg="#0072CE"
            btnText={"Proceed to Application"}
          />
        </div>
      </div>
    </Container>
  );
};